import keycloak from "./Keycloak";

const _kc = keycloak;
const EMPTY_ADDRESS = {
  street: "",
  city: "",
  state: "",
  zipCode: "",
};

const getClaimArrayValue = (claim) => {
  if (Array.isArray(claim)) {
    return claim[0] ?? "";
  }

  return claim ?? "";
};

/**
 * Initializes Keycloak instance and calls the provided callback function if successfully authenticated.
 *
 * @param onAuthenticatedCallback
 */
const initKeycloak = (onAuthenticatedCallback) => {
  _kc
    .init({
      onLoad: "check-sso",
      silentCheckSsoRedirectUri: `${globalThis.location.origin}/silent-check-sso/index.html`,
      pkceMethod: "S256",
      checkLoginIframe: false, // Disabled to avoid issues with cross-origin iframe checks
    })
    .then((authenticated) => {
      if (!authenticated) {
        console.log("user is not authenticated..!");
      }
      onAuthenticatedCallback();
    })
    .catch(console.error);
};

const doLogin = async (setUserRefreshFlag) => {
  await _kc.login({ redirectUri: globalThis.location.href });
  if (isLoggedIn()) {
    setUserRefreshFlag((prev) => !prev);
  }
};

/**
 * Check if the location route is '/account' or its child and if so, log the user out and redirect to the home page.
 * else just log the user out.
 *
 * @param {Location} location
 */
const doLogout = async (location) => {
  if (location.pathname.includes("account")) {
    await _kc.logout({ redirectUri: globalThis.location.origin });
  } else {
    await _kc.logout();
  }
};

const getToken = () => _kc.token;

const isLoggedIn = () => !!_kc.token;

const updateToken = (successCallback) =>
  _kc.updateToken(5).then(successCallback).catch(() => doLogin(() => {}));

const getUserId = () => _kc.tokenParsed?.sub;

const getUsername = () => _kc.tokenParsed?.preferred_username;

const getUserFirstName = () => _kc.tokenParsed?.given_name;

const getUserLastName = () => _kc.tokenParsed?.family_name;

const hasRole = (roles) => Boolean(roles?.some((role) => _kc.hasRealmRole(role)));

const loadUserProfile = () => _kc.loadUserProfile();

const getAddress = () => {
  const addressClaim = _kc.tokenParsed?.address;

  if (Array.isArray(addressClaim) && addressClaim.length === 4) {
    return {
      street: addressClaim[0],
      city: addressClaim[1],
      state: addressClaim[2],
      zipCode: addressClaim[3],
    };
  }

  if (addressClaim && typeof addressClaim === "object") {
    return {
      street: addressClaim.street_address ?? "",
      city: addressClaim.locality ?? "",
      state: addressClaim.region ?? "",
      zipCode: addressClaim.postal_code ?? "",
    };
  }

  return EMPTY_ADDRESS;
};

const getUserInfo = () => {
  const address = getAddress();
  const hasAddress = Object.values(address).some(Boolean);
  const userInfo = {
    id: getUserId(),
    userId: getUserId(),
    username: getUsername(),
    firstName: getUserFirstName(),
    lastName: getUserLastName(),
    email: _kc.tokenParsed?.email ?? "",
    phone: getClaimArrayValue(_kc.tokenParsed?.phone),
    dateOfBirth: getClaimArrayValue(_kc.tokenParsed?.dob),
    gender: getClaimArrayValue(_kc.tokenParsed?.gender),
    address: hasAddress ? address : "",
  };
  return userInfo;
};

const KeycloakService = {
  initKeycloak,
  doLogin,
  doLogout,
  isLoggedIn,
  getToken,
  getUserId,
  getUserInfo,
  updateToken,
  getUsername,
  getUserFirstName,
  getUserLastName,
  hasRole,
  loadUserProfile,
};

export default KeycloakService;
